"use client"

import type React from "react"
import { useEffect, useState, type ReactNode } from "react"
import { useSearchParamsSafe as useSearchParams } from "@/lib/hooks/use-search-params-safe"
import { ForceLightTheme } from "@/components/providers/theme-provider"
import { useNavigate } from "@/lib/navigation"
import { useToast } from "@/lib/hooks/use-toast"
import apiClient from "@/lib/api/api-client"
import { SiteHeader } from "@/components/navigation/site-header"
import { MarketingLoginModal } from "@/components/pages/landing/marketing-login-modal"

type MarketingLayoutProps = {
  children: ReactNode
  footer?: ReactNode
}

export function MarketingLayout({ children, footer }: MarketingLayoutProps) {
  const navigate = useNavigate()
  const searchParams = useSearchParams()
  const { toast } = useToast()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const expired = searchParams?.get("sessionExpired")
    const shouldOpenLogin = searchParams?.get("login")
    if (expired === "1") {
      toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" })
    }
    if (shouldOpenLogin === "1") {
      setShowLogin(true)
    }
  }, [searchParams, toast])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      await apiClient.auth.login(email, password)
      setShowLogin(false)
      setError(null)
      navigate("/dashboard", { replace: true })
    } catch (loginError) {
      console.error("Login error:", loginError)
      setError(loginError instanceof Error ? loginError.message : "Incorrect email or password")
    } finally {
      setIsLoading(false)
    }
  }

  const closeLogin = () => {
    setShowLogin(false)
    setError(null)
  }

  return (
    <div className="light min-h-screen">
      <ForceLightTheme />
      <SiteHeader onLoginClick={() => setShowLogin(true)} />

      {showLogin && (
        <MarketingLoginModal
          email={email}
          password={password}
          isLoading={isLoading}
          error={error}
          onEmailChange={(value) => {
            setEmail(value)
            setError(null)
          }}
          onPasswordChange={(value) => {
            setPassword(value)
            setError(null)
          }}
          onSubmit={handleLogin}
          onClose={closeLogin}
        />
      )}

      {children}
      {footer}
    </div>
  )
}
